import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowUp,
  BookOpenText,
  ChevronDown,
  Keyboard,
  Lightbulb,
  MessageSquareText,
  Mic,
  MicOff,
  RefreshCw,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';

import { Button } from 'components/ui/button';
import { AppDialog } from 'components/ui/app-dialog';
import { PersonaPortrait } from './PersonaPortrait';
import type { RoleplaySessionController } from './useRoleplaySession';
import {
  useRoleplayVoiceSession,
  type RoleplayVoiceLatency,
} from './useRoleplayVoiceSession';

const formatTimeLeft = (secondsLeft: number) =>
  `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`;

const TRANSCRIPT_FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export const selectRoleplayPersonaLine = (
  liveActorTranscript: string,
  activityLabel: string,
  latestPersistedLine: string,
) => liveActorTranscript || latestPersistedLine || activityLabel;

export const RoleplayPersonaCaption: React.FC<{
  turns: { key: string; text: string }[];
  personaName: string;
}> = ({ turns, personaName }) => {
  const captionRef = useRef<HTMLParagraphElement | null>(null);
  const followCaptionRef = useRef(true);
  const text = turns.map((turn) => turn.text).join('\n');

  useEffect(() => {
    const caption = captionRef.current;
    if (caption && followCaptionRef.current) caption.scrollTop = caption.scrollHeight;
  }, [text]);

  return (
    <p
      ref={captionRef}
      className={`rp-persona-line max-h-[min(25dvh,13rem)] w-fit overflow-y-auto overscroll-contain text-foundation-base-darkbase-dark-700 ${text.length > 220 ? 'rp-persona-line--long' : ''}`}
      role="region"
      aria-label={`${personaName}'s response`}
      tabIndex={0}
      onScroll={(event) => {
        const caption = event.currentTarget;
        followCaptionRef.current = caption.scrollHeight - caption.clientHeight - caption.scrollTop <= 24;
      }}
      data-testid="roleplay-persona-line"
    >
      {turns.map((turn, index) => (
        <span key={turn.key} className={`block ${index > 0 ? 'mt-3' : ''}`} data-testid="roleplay-persona-turn">
          {turn.text}
        </span>
      ))}
    </p>
  );
};

const PracticeCue: React.FC<{ hint: string }> = ({ hint }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <aside
      className={`rp-coach-cue ${expanded ? 'rp-coach-cue--expanded' : 'rp-coach-cue--collapsed'}`}
      role="status"
      aria-live="polite"
      data-testid="roleplay-coach-cue"
    >
      <Lightbulb size={17} aria-hidden="true" />
      <div>
        <div className="rp-coach-cue-heading">
          <span>Practice cue</span>
          <button
            type="button"
            className="rp-coach-cue-toggle"
            onClick={() => setExpanded((isExpanded) => !isExpanded)}
            aria-expanded={expanded}
            aria-label={expanded ? 'Minimize practice cue' : 'Show practice cue'}
            data-testid="roleplay-coach-cue-toggle"
          >
            <ChevronDown size={16} aria-hidden="true" />
          </button>
        </div>
        <p>{hint}</p>
      </div>
    </aside>
  );
};

export const PracticeScreen: React.FC<{
  controller: RoleplaySessionController;
  onVoiceLatencyChange?: (latency: RoleplayVoiceLatency | null) => void;
}> = ({ controller, onVoiceLatencyChange }) => {
  const { scenario, session } = controller;
  const { processing, setRuntimeWaiting } = controller;
  const [typed, setTyped] = useState('');
  const [showTranscript, setShowTranscript] = useState(false);
  const [transcriptClosing, setTranscriptClosing] = useState(false);
  const [showScenario, setShowScenario] = useState(false);
  const [showEndConfirmation, setShowEndConfirmation] = useState(false);
  const [textComposerActive, setTextComposerActive] = useState(() => session?.mode === 'text');
  const [submittingText, setSubmittingText] = useState(false);
  const [textAnnouncement, setTextAnnouncement] = useState({ key: '', text: '' });
  const textInputRef = useRef<HTMLInputElement | null>(null);
  const composerRef = useRef<HTMLFormElement | null>(null);
  const submittingTextRef = useRef(false);
  const composingRef = useRef(false);
  const restoreComposerFocusRef = useRef(false);
  const announcedTextTurnsRef = useRef(new Set<string>());
  const transcriptTriggerRef = useRef<HTMLButtonElement | null>(null);
  const transcriptDialogRef = useRef<HTMLDivElement | null>(null);
  const transcriptCloseRef = useRef<HTMLButtonElement | null>(null);
  const transcriptRestoreFocusRef = useRef<HTMLElement | null>(null);
  const transcriptCloseTimerRef = useRef<number | null>(null);
  const transcriptClosingRef = useRef(false);
  const persona = scenario?.persona;
  const learnerHint = session?.win_evaluation.outcome === 'continue'
    ? session.win_evaluation.learner_hint?.trim() ?? ''
    : '';

  const voice = useRoleplayVoiceSession({
    flowName: controller.flowName,
    session,
    personaName: persona?.name ?? 'the persona',
    onLearnerTranscript: (transcript, eventId, onProgress) => controller.submitTurn(transcript, 'voice', eventId, undefined, onProgress),
    onActorTurn: controller.persistActorTurn,
    onTerminalPlaybackComplete: controller.releaseTerminalTransition,
    onRuntimeClaim: controller.claimRuntime,
    onRuntimeSuspend: controller.suspendRuntime,
    onRuntimeReady: controller.readyRuntime,
    onInputFinalizing: controller.inputFinalizing,
    onRuntimeSession: controller.applyRuntimeSession,
    onLatencyChange: onVoiceLatencyChange,
  });
  const { cancelAndDisconnect } = voice;

  useEffect(() => {
    // Focus landing on the body is the browser dropping a disabled control,
    // not the learner choosing another one.
    const cancelFocusRestore = (event: Event) => {
      if (
        event.target instanceof Node
        && event.target !== document.body
        && !composerRef.current?.contains(event.target)
      ) {
        restoreComposerFocusRef.current = false;
      }
    };
    document.addEventListener('focusin', cancelFocusRestore);
    document.addEventListener('pointerdown', cancelFocusRestore);
    return () => {
      document.removeEventListener('focusin', cancelFocusRestore);
      document.removeEventListener('pointerdown', cancelFocusRestore);
    };
  }, []);

  useEffect(() => {
    const input = textInputRef.current;
    if (restoreComposerFocusRef.current && !submittingText && input && !input.disabled) {
      restoreComposerFocusRef.current = false;
      input.focus();
    }
  });

  useEffect(() => {
    if (session?.mode !== 'text' || processing || session.turn_processing || session.actor_directive) return;
    const completedTurns = controller.latestPersonaTurns.filter((turn) => (
      !turn.metadata?.safety_only && !announcedTextTurnsRef.current.has(`${session.id}:${turn.id}`)
    ));
    if (!completedTurns.length) return;
    completedTurns.forEach((turn) => announcedTextTurnsRef.current.add(`${session.id}:${turn.id}`));
    setTextAnnouncement({
      key: completedTurns.map((turn) => `${session.id}:${turn.id}`).join(','),
      text: `${persona?.name ?? 'The persona'}: ${completedTurns.map((turn) => turn.text).join('\n')}`,
    });
  }, [controller.latestPersonaTurns, persona?.name, processing, session]);

  const openTranscript = useCallback(() => {
    if (transcriptCloseTimerRef.current !== null) {
      window.clearTimeout(transcriptCloseTimerRef.current);
      transcriptCloseTimerRef.current = null;
    }
    transcriptRestoreFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : transcriptTriggerRef.current;
    transcriptClosingRef.current = false;
    setTranscriptClosing(false);
    setShowTranscript(true);
  }, []);

  const closeTranscript = useCallback(() => {
    if (transcriptClosingRef.current) return;
    transcriptClosingRef.current = true;
    setTranscriptClosing(true);
    transcriptCloseTimerRef.current = window.setTimeout(() => {
      setShowTranscript(false);
      setTranscriptClosing(false);
      transcriptClosingRef.current = false;
      transcriptCloseTimerRef.current = null;
      window.requestAnimationFrame(() => {
        (transcriptRestoreFocusRef.current ?? transcriptTriggerRef.current)?.focus();
      });
    }, 180);
  }, []);

  useEffect(() => {
    const scrollContainer = document.getElementById('root');
    if (scrollContainer) {
      const previousOverflow = scrollContainer.style.overflow;
      scrollContainer.style.overflow = 'hidden';
      scrollContainer.scrollTop = 0;
      scrollContainer.scrollLeft = 0;
      return () => {
        scrollContainer.style.overflow = previousOverflow;
      };
    }
    return undefined;
  }, []);

  useEffect(() => {
    if (session?.status === 'crisis_paused' || session?.status === 'safety_paused') {
      cancelAndDisconnect();
    }
  }, [cancelAndDisconnect, session?.status]);

  useEffect(() => {
    if (
      controller.terminalTransitionPending
      && session?.mode === 'voice'
      && !voice.isPlaybackActive
      && voice.state !== 'responding'
      && !session?.actor_directive
    ) {
      controller.releaseTerminalTransition();
    }
  }, [
    controller,
    session?.actor_directive,
    session?.mode,
    voice.isPlaybackActive,
    voice.state,
  ]);

  useEffect(() => {
    const voiceWait = [
      'finalizing',
      'thinking',
      'responding',
      'speaking',
    ].includes(voice.activity);
    setRuntimeWaiting(
      processing || voiceWait || Boolean(session?.actor_directive),
    );
    return () => setRuntimeWaiting(false);
  }, [processing, session?.actor_directive, setRuntimeWaiting, voice.activity]);

  useEffect(() => {
    if (session?.mode === 'text') setTextComposerActive(true);
  }, [session?.mode]);

  useEffect(() => () => {
    if (transcriptCloseTimerRef.current !== null) {
      window.clearTimeout(transcriptCloseTimerRef.current);
    }
  }, []);

  useEffect(() => {
    if (!showTranscript || transcriptClosing) return undefined;
    const focusFrame = window.requestAnimationFrame(() => transcriptCloseRef.current?.focus());
    const handleTranscriptKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeTranscript();
        return;
      }
      if (event.key !== 'Tab' || !transcriptDialogRef.current) return;
      const focusable = Array.from(
        transcriptDialogRef.current.querySelectorAll<HTMLElement>(
          TRANSCRIPT_FOCUSABLE_SELECTOR,
        ),
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) {
        event.preventDefault();
        transcriptDialogRef.current.focus();
        return;
      }
      const activeElement = document.activeElement;
      if (
        event.shiftKey
        && (activeElement === first || !transcriptDialogRef.current.contains(activeElement))
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey
        && (activeElement === last || !transcriptDialogRef.current.contains(activeElement))
      ) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleTranscriptKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleTranscriptKeyDown);
    };
  }, [closeTranscript, showTranscript, transcriptClosing]);

  if (!scenario || !session || !persona) return null;

  const visibleTurns = session.turns.filter((turn) => !turn.metadata?.safety_only);
  const isClarification = session.status === 'safety_paused';
  const isSupportive = session.ui_mode === 'supportive_distress';
  const hasPendingActor = Boolean(session.actor_directive);
  const isPreparedVoiceOpening = session.mode === 'voice'
    && session.actor_directive?.purpose === 'opening';
  const displayedExpression = session.actor_directive?.purpose === 'opening'
    && session.actor_directive.fixed_expression
    ? session.actor_directive.fixed_expression
    : session.current_expression;
  const voiceConnected = voice.state === 'connected' || voice.state === 'responding';
  const voiceBlocksText = !textComposerActive && [
    'finalizing',
    'thinking',
    'responding',
    'speaking',
    'needs_action',
  ].includes(voice.activity);
  const voiceModeSwitchBlocked = [
    'connecting',
    'finalizing',
    'thinking',
    'responding',
    'speaking',
  ].includes(voice.activity);
  // While Voice is connected, only the hook knows whether the transport is open,
  // no directive is in flight and learner input is accepted; the placeholder
  // copy is never the source of truth for the Send control.
  const canRespond = session.status === 'active'
    && !controller.processing
    && !submittingText
    && !hasPendingActor
    && !session.actor_error
    && !voiceBlocksText
    && (!voiceConnected || voice.canSubmitText);
  const submitText = async (event: React.FormEvent) => {
    event.preventDefault();
    const message = typed.trim();
    if (!message || !canRespond || composingRef.current || submittingTextRef.current) return;
    const submittedDraft = typed;
    // Only a learner who submitted from the composer (or whose focus the
    // disabled control dropped to the body) gets it back afterwards.
    restoreComposerFocusRef.current = document.activeElement === document.body
      || Boolean(composerRef.current?.contains(document.activeElement));
    submittingTextRef.current = true;
    setSubmittingText(true);
    try {
      const accepted = voiceConnected
        ? await voice.submitTypedTurn((onProgress) => controller.submitTurn(message, 'text', undefined, 'voice', onProgress))
        : await controller.submitTurn(message, 'text', undefined, 'text');
      // A rejected, aborted, offline or null send keeps the draft; the
      // controller retains the request identity for an explicit retry.
      if (accepted?.id === session.id) {
        setTyped((current) => current === submittedDraft ? '' : current);
      }
    } finally {
      submittingTextRef.current = false;
      setSubmittingText(false);
    }
  };
  const continueByTyping = () => {
    voice.muteMicrophone();
    setTextComposerActive(true);
    window.setTimeout(() => textInputRef.current?.focus(), 0);
  };
  const startVoice = async () => {
    await voice.connect();
  };
  const confirmEndPractice = async () => {
    setShowEndConfirmation(false);
    cancelAndDisconnect();
    await controller.endPractice();
  };
  const continuePendingActorByText = async () => {
    cancelAndDisconnect();
    setTextComposerActive(true);
    controller.releaseTerminalTransition();
    await controller.retryActor();
  };
  const toggleTextComposer = () => {
    if (textComposerActive) {
      setTextComposerActive(false);
      return;
    }
    if (hasPendingActor) void continuePendingActorByText();
    else continueByTyping();
  };
  const canStartVoice = !controller.processing
    && !session.actor_error
    && (session.status === 'active' || (session.status === 'closing' && hasPendingActor));
  const showLearnerHint = Boolean(learnerHint)
    && session.status === 'active'
    && !controller.processing
    && !hasPendingActor
    && !session.actor_error
    && ['idle', 'listening'].includes(voice.activity);
  const latestPersistedLine = controller.latestPersonaTurn?.text || '';
  const isTextClosingVisible = session.mode === 'text'
    && (session.status === 'completed' || session.status === 'ended_early')
    && controller.terminalTransitionPending;

  let activityLabel = '';
  if (session.status === 'evaluation_paused') activityLabel = 'We couldn’t continue just yet. Your message is saved.';
  else if (session.status === 'closing') activityLabel = `${persona.name} is wrapping up the conversation…`;
  else if (session.actor_error) activityLabel = `${persona.name} could not respond yet.`;
  else if (controller.processing) activityLabel = `${persona.name} is thinking…`;
  else if (hasPendingActor || voice.state === 'responding') {
    activityLabel = `${persona.name} is responding…`;
  }

  const voiceStatusLabel: Record<typeof voice.activity, string> = {
    idle: isPreparedVoiceOpening ? 'Getting voice ready' : 'Voice off',
    connecting: 'Connecting voice',
    listening: voice.isMicrophoneMuted ? 'Microphone muted' : 'Listening',
    finalizing: 'Finishing your response',
    thinking: `${persona.name} is thinking`,
    responding: voice.liveActorTranscript
      ? 'Your turn is next'
      : isPreparedVoiceOpening
        ? `${persona.name} is beginning the conversation`
        : `${persona.name} is preparing a reply`,
    speaking: `${persona.name} is speaking`,
    needs_action: 'Voice needs your attention',
  };

  // Realtime actor transcript deltas must win over the generic activity label so
  // captions appear while Maya is speaking, not only after the response finishes.
  const displayedLine = session.mode === 'voice'
    ? voice.liveActorTranscript || (!hasPendingActor ? latestPersistedLine : '')
    : selectRoleplayPersonaLine('', activityLabel, latestPersistedLine);
  const displayedPersonaTurns = session.mode === 'text' && controller.latestPersonaTurns.length
    ? controller.latestPersonaTurns.map((turn) => ({ key: `turn-${turn.id}`, text: turn.text }))
    : displayedLine
      ? [{ key: session.mode === 'voice' ? 'voice-live' : 'activity', text: displayedLine }]
      : [];
  const showVoiceOpeningPlaceholder = isPreparedVoiceOpening && !voice.liveActorTranscript;
  const learnerCaption = voice.liveTranscript
    ? `You: ${voice.liveTranscript}`
    : voice.isLearnerSpeaking
      ? 'Listening…'
      : '';
  const inputPlaceholder = isTextClosingVisible
    ? 'Practice complete'
    : voice.activity === 'finalizing'
      ? 'Finishing your voice response…'
      : voice.activity === 'thinking' || voice.activity === 'responding' || voice.activity === 'speaking'
        ? `Wait for ${persona.name} to finish…`
        : `Type your response to ${persona.name}…`;
  return (
    <div className="rp-practice-shell relative h-[calc(100dvh-1rem)] min-h-0 w-full overflow-hidden" data-testid="roleplay-practice">
      <div className="rp-practice-header">
        <button
          type="button"
          onClick={() => setShowEndConfirmation(true)}
          className="rp-end-practice min-w-max whitespace-nowrap"
          aria-label="End practice"
          data-testid="roleplay-end-practice"
        >
          <X size={16} aria-hidden="true" />
          <span className="rp-end-label-full" aria-hidden="true">End practice</span>
          <span className="rp-end-label-short" aria-hidden="true">End</span>
        </button>
        <span className="whitespace-nowrap font-mono text-xs tabular-nums text-foundation-base-darkbase-dark-400" data-testid="roleplay-limits">
          <span className="sr-only" data-testid="roleplay-limits-accessible">
            {formatTimeLeft(controller.secondsLeft)} left · {session.turns_left} {session.turns_left === 1 ? 'turn' : 'turns'} left
          </span>
          <span className="rp-limit-label-full" aria-hidden="true" data-testid="roleplay-limits-full">
            {formatTimeLeft(controller.secondsLeft)} left · {session.turns_left} {session.turns_left === 1 ? 'turn' : 'turns'} left
          </span>
          <span className="rp-limit-label-short" aria-hidden="true" data-testid="roleplay-limits-short">
            {formatTimeLeft(controller.secondsLeft)} · {session.turns_left} {session.turns_left === 1 ? 'turn' : 'turns'}
          </span>
        </span>
        <button
          type="button"
          onClick={() => setShowScenario(true)}
          className="rp-view-scenario"
          aria-label="View scenario"
          data-testid="roleplay-view-scenario"
        >
          <BookOpenText size={16} aria-hidden="true" />
          <span className="rp-scenario-label-full">View scenario</span>
          <span className="rp-scenario-label-short" aria-hidden="true">Scenario</span>
        </button>
      </div>

      {(isSupportive || showLearnerHint) && (
        <div className="rp-practice-notices">
          {isSupportive && (
            <div className="rp-supportive-notice" data-testid="roleplay-supportive-banner">
              <p className="type-body-s-semibold text-foundation-base-darkbase-dark-700">Support is available outside this practice.</p>
              <p className="type-body-s text-foundation-base-darkbase-dark-500">Keep practicing, or end whenever you need to.</p>
            </div>
          )}
          {showLearnerHint && <PracticeCue key={learnerHint} hint={learnerHint} />}
        </div>
      )}

      <div className="rp-practice-stage grid min-h-0 w-full max-w-[35rem] flex-1 gap-2 py-2">
        <div className="rp-portrait-anchor flex min-h-0 items-center justify-center" data-testid="roleplay-portrait-anchor">
          <div className="rp-portrait-stage relative aspect-square">
            {(controller.processing || ['thinking', 'responding', 'speaking'].includes(voice.activity)) && <div className="rp-speaking-ring absolute inset-[6%] rounded-full" aria-hidden="true" />}
            <PersonaPortrait
              persona={persona}
              expression={displayedExpression}
              className="relative h-full w-full rounded-full"
            />
          </div>
        </div>
        <div
          className="rp-practice-dialogue flex min-h-0 flex-col items-center justify-center gap-2"
          data-testid="roleplay-dialogue-stage"
        >
          {(session.mode === 'voice' || voice.state !== 'idle') && (
            <div className={`rp-voice-status rp-voice-status--${voice.isMicrophoneMuted && voice.activity === 'listening' ? 'muted' : voice.activity}`} role="status" aria-live="polite" data-testid="roleplay-voice-status">
              <span className="rp-voice-status-dot" aria-hidden="true" />
              {voiceStatusLabel[voice.activity]}
            </div>
          )}
          {session.mode !== 'voice' && activityLabel && (
            <div className="rp-voice-status rp-voice-status--thinking" role="status" aria-live="polite" data-testid="roleplay-activity-status">
              <span className="rp-voice-status-dot" aria-hidden="true" />
              {activityLabel}
            </div>
          )}
          {showVoiceOpeningPlaceholder ? (
            <div className="rp-opening-placeholder" data-testid="roleplay-opening-placeholder">
              <Mic size={18} aria-hidden="true" />
              <span>{`${persona.name} will begin when voice is ready.`}</span>
            </div>
          ) : displayedPersonaTurns.length ? (
            <RoleplayPersonaCaption key={session.id} turns={displayedPersonaTurns} personaName={persona.name} />
          ) : null}
          {(learnerCaption || controller.error || voice.error) && (
            <div
              className={`rp-live-transcription ${controller.error || voice.error ? 'rp-live-transcription--error' : ''}`}
              role={controller.error || voice.error ? 'alert' : 'status'}
              data-testid="roleplay-live-learner-transcript"
            >
              <span className="rp-live-transcription-icon" aria-hidden="true">{controller.error && textComposerActive ? <Keyboard size={15} /> : <Mic size={15} />}</span>
              <div>
                <span className="rp-live-transcription-label">{controller.error && textComposerActive ? 'Your message needs attention' : controller.error || voice.error ? 'Voice needs attention' : 'Your voice'}</span>
                <p>{controller.error || voice.error || learnerCaption.replace(/^You:\s*/, '')}</p>
              </div>
            </div>
          )}
          <span className="sr-only" aria-live="polite" aria-atomic="true" data-testid="roleplay-completed-caption">
            {session.mode === 'text' ? <span key={textAnnouncement.key}>{textAnnouncement.text}</span> : voice.captionAnnouncement}
          </span>
          {voice.activity === 'needs_action' && (
            <div className="rp-voice-recovery" data-testid="roleplay-voice-recovery">
              <p className="type-body-s text-foundation-base-darkbase-dark-500">
                {voice.pendingTranscript
                  ? 'Review the transcript below, then send it or switch input methods.'
                  : 'Voice did not finish cleanly. Choose how you would like to continue.'}
              </p>
              {voice.pendingTranscript && (
                <p className="rp-voice-recovery-transcript">“{voice.pendingTranscript.text}”</p>
              )}
              <div className="flex flex-wrap justify-center gap-2">
                {voice.pendingTranscript && (
                  <Button onClick={voice.submitPendingTranscript} size="auto" data-testid="roleplay-send-pending-transcript">
                    Send this transcript
                  </Button>
                )}
                <Button variant="secondary" onClick={voice.retryListening} size="auto" data-testid="roleplay-retry-listening">
                  Try voice again
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    if (hasPendingActor) void continuePendingActorByText();
                    else continueByTyping();
                  }}
                  size="auto"
                  data-testid="roleplay-recovery-type"
                >
                  Continue by typing
                </Button>
              </div>
            </div>
          )}
          {session.status === 'evaluation_paused' && (
            <Button onClick={() => void controller.retryEvaluation()} disabled={controller.processing} size="auto" data-testid="roleplay-evaluation-retry">
              <RefreshCw size={17} /> Try again
            </Button>
          )}
          {isTextClosingVisible && (
            <Button
              onClick={controller.releaseTerminalTransition}
              size="auto"
              data-testid="roleplay-continue-reflection"
            >
              Continue to reflection
            </Button>
          )}
          {session.mode === 'text' && (session.actor_error || hasPendingActor) && (
            <Button onClick={() => void controller.retryActor()} disabled={controller.processing} size="auto" data-testid="roleplay-actor-retry">
              <RefreshCw size={17} /> Retry response
            </Button>
          )}
        </div>
      </div>

      <div className="rp-practice-controls flex w-full max-w-[42rem] flex-col items-center gap-3">
        {textComposerActive && (
          <form ref={composerRef} onSubmit={(event) => void submitText(event)} className="flex w-full max-w-[28rem] gap-2" data-testid="roleplay-text-composer">
            <input
              ref={textInputRef}
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              onCompositionStart={() => { composingRef.current = true; }}
              onCompositionEnd={() => { composingRef.current = false; }}
              onKeyDown={(event) => {
                // Enter while an IME composition is open only confirms the
                // candidate. Browsers report it through isComposing, the legacy
                // 229 keyCode, or a composition that has not ended yet.
                if (event.key === 'Enter' && (composingRef.current || event.nativeEvent.isComposing || event.keyCode === 229)) {
                  event.preventDefault();
                }
              }}
              disabled={!canRespond}
              placeholder={inputPlaceholder}
              aria-label={`Your response to ${persona.name}`}
              className="h-[52px] min-w-0 flex-1 rounded-full border border-surface-sky-end bg-white px-[18px] text-[0.9375rem] outline-none focus:border-foundation-orange-400"
              data-testid="roleplay-text-input"
            />
            <button type="submit" aria-label="Send" disabled={!typed.trim() || !canRespond} className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-full bg-foundation-orange-500 text-white disabled:opacity-50" data-testid="roleplay-send"><ArrowUp size={20} /></button>
          </form>
        )}

        <div className="rp-control-actions flex flex-wrap items-center justify-center gap-2" data-testid="roleplay-control-dock">
          {voiceConnected ? (
            <Button
              variant="secondary"
              size="auto"
              onClick={voice.toggleMicrophoneMute}
              className="rp-voice-primary-control"
              aria-pressed={voice.isMicrophoneMuted}
              data-testid="roleplay-microphone-toggle"
            >
              {voice.isMicrophoneMuted ? <Mic size={17} /> : <MicOff size={17} />}
              {voice.isMicrophoneMuted ? 'Unmute mic' : 'Mute mic'}
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="auto"
              onClick={() => void startVoice()}
              disabled={voice.state === 'connecting' || !canStartVoice}
              className="rp-voice-primary-control"
              aria-label={voice.state === 'connecting' ? 'Connecting voice' : 'Start voice'}
              data-testid="roleplay-voice-start"
            >
              <Mic size={17} />
              {voice.state === 'connecting' ? (
                <span>
                  <span className="rp-connecting-label-full">Connecting…</span>
                  <span className="rp-connecting-label-short" aria-hidden="true">Wait…</span>
                </span>
              ) : 'Start voice'}
            </Button>
          )}
          <Button
            variant="ghost"
            size="auto"
            onClick={voice.togglePersonaMute}
            disabled={!voiceConnected}
            className="rp-mode-control rp-persona-audio-control"
            aria-pressed={voice.isPersonaMuted}
            data-testid="roleplay-persona-audio-toggle"
          >
            {voice.isPersonaMuted ? <Volume2 size={17} /> : <VolumeX size={17} />}
            {voice.isPersonaMuted ? `Unmute ${persona.name}` : `Mute ${persona.name}`}
          </Button>
          <Button
            variant="ghost"
            size="auto"
            onClick={toggleTextComposer}
            disabled={voiceModeSwitchBlocked || controller.processing}
            className="rp-mode-control rp-typing-control"
            aria-label="Text mode"
            aria-pressed={textComposerActive}
            data-testid="roleplay-continue-by-typing"
          >
            <Keyboard size={17} />
            <span className="rp-typing-label" aria-hidden="true">Text mode</span>
          </Button>
          <Button
            type="button"
            ref={transcriptTriggerRef}
            variant="ghost"
            size="auto"
            onClick={openTranscript}
            className="rp-mode-control rp-transcript-control"
            aria-expanded={showTranscript && !transcriptClosing}
            data-testid="roleplay-transcript-toggle"
          >
            <MessageSquareText size={17} /> Transcript
          </Button>
        </div>
      </div>

      {showTranscript && (
        <div
          ref={transcriptDialogRef}
          className={`rp-transcript-backdrop ${transcriptClosing ? 'rp-transcript-backdrop--closing' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-label="Conversation transcript"
          tabIndex={-1}
          data-testid="roleplay-transcript-sheet"
        >
          <div className="rp-transcript-sheet">
            <div className="rp-transcript-header">
              <div>
                <span className="rp-transcript-eyebrow"><MessageSquareText size={14} aria-hidden="true" /> Voice transcript</span>
                <h2>Conversation</h2>
              </div>
              <button ref={transcriptCloseRef} type="button" onClick={closeTranscript} className="rp-transcript-close" aria-label="Close transcript" data-testid="roleplay-transcript-close"><X size={18} aria-hidden="true" /></button>
            </div>
            <div className="rp-transcript-list" role="region" aria-label="Saved conversation" tabIndex={0}>
              {visibleTurns.length ? visibleTurns.map((turn) => (
                <div className={`rp-transcript-turn rp-transcript-turn--${turn.speaker}`} key={turn.id}>
                  <span className="rp-transcript-speaker">
                    {turn.speaker === 'persona' ? persona.name : 'You'}
                  </span>
                  <p>{turn.text}</p>
                </div>
              )) : (
                <p className="rp-transcript-empty">The conversation will appear here as you speak.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {showScenario && (
        <AppDialog
          onClose={() => setShowScenario(false)}
          title="Practice scenario"
          description={scenario.subtitle || `Review what you are practicing with ${persona.name}.`}
          size="sm"
          bodyClassName="rp-scenario-recap"
          data-testid="roleplay-scenario-dialog"
          actions={(
            <Button onClick={() => setShowScenario(false)} size="auto" data-testid="roleplay-scenario-close">
              Back to practice
            </Button>
          )}
        >
          <section>
            <span>The situation</span>
            <p>{scenario.situation}</p>
          </section>
          <section>
            <span>Your role</span>
            <p>{scenario.learner_role}</p>
          </section>
          <section>
            <span>What to practice</span>
            <ul>
              {scenario.learning_objectives.map((objective) => <li key={objective}>{objective}</li>)}
            </ul>
          </section>
        </AppDialog>
      )}

      {showEndConfirmation && (
        <AppDialog
          onClose={() => setShowEndConfirmation(false)}
          title="End this practice?"
          description="Your conversation so far will be saved and you can review feedback before trying again."
          size="sm"
          actions={(
            <>
              <Button variant="secondary" onClick={() => setShowEndConfirmation(false)} size="auto">Keep practicing</Button>
              <Button onClick={() => void confirmEndPractice()} size="auto" data-testid="roleplay-confirm-end">End practice</Button>
            </>
          )}
        />
      )}

      {isClarification && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-foundation-base-darkbase-dark-700/45 p-5 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Outside the practice" data-testid="roleplay-safety-overlay">
          <div className="flex w-full max-w-[28.75rem] flex-col gap-3.5 rounded-[20px] bg-white px-6 py-[26px] shadow-xl">
            <span className="font-mono text-[0.6875rem] uppercase tracking-[0.1em] text-foundation-base-darkbase-dark-300">Outside the practice</span>
            <p className="rp-safety-question text-foundation-base-darkbase-dark-700">
              We paused for a moment. Were you talking about {persona.name}, or about yourself?
            </p>
            <p className="type-body-s text-foundation-base-darkbase-dark-400">
              Whatever the answer, it is okay. If this is about you, the practice will pause and show safety guidance.
            </p>
            <Button onClick={() => void controller.resolveSafety('persona')} disabled={controller.processing} data-testid="roleplay-safety-resume">
              I meant {persona.name} — continue
            </Button>
            <Button variant="secondary" onClick={() => void controller.resolveSafety('self')} disabled={controller.processing} data-testid="roleplay-safety-self">I’m talking about myself</Button>
          </div>
        </div>
      )}
    </div>
  );
};
